/**
 * Notification utilities — pure functions shared by consts.ts and notification.tsx.
 * Kept separate to avoid circular imports.
 */

import type {
  NotificationIntent,
  NotificationLayout,
  NotificationVariantHierarchy,
  NotificationVariantLegacy,
} from './notification.types'

/** Canonical hierarchy keys recognised by the two-prop API. */
const HIERARCHY_KEYS = new Set<string>(['inline', 'banner'])

/** The banner hierarchy key. */
const BANNER_HIERARCHY = 'banner'

/** Default intent applied when none is specified. */
export const DEFAULT_INTENT: NotificationIntent = 'info'

/** Default variant hierarchy applied when none is specified. */
export const DEFAULT_VARIANT: NotificationVariantHierarchy = 'inline'

/**
 * Capitalises the first character of a string.
 * Mirrors button/utils.ts — used to compose theme keys the same way theme.ts generates them.
 */
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export interface ResolvedNotificationVariant {
  /** The theme key to pass to useStyleConfig (e.g. `'inlineInfo'`, `'bannerDanger'`). */
  themeKey: string
  /** True when the resolved variant is a banner hierarchy. */
  isBanner: boolean
}

/**
 * Converts a two-prop variant+intent pair into the combined theme key used internally
 * (e.g. `resolveNotificationVariant('inline', 'info') => { themeKey: 'inlineInfo', isBanner: false }`).
 * When `intent` is omitted, `DEFAULT_INTENT` ('info') is used.
 *
 * If `variant` is already a fully-qualified legacy key (e.g. `'inlineBlue'`, `'bannerRed'`)
 * it is returned unchanged so the new API is backward-compatible.
 *
 * Returns `{ themeKey: 'inlineInfo', isBanner: false }` (the default) when `variant` is absent.
 */
export const resolveNotificationVariant = (
  variant: NotificationVariantHierarchy | NotificationVariantLegacy | string | undefined = DEFAULT_VARIANT,
  intent: NotificationIntent | string | undefined = DEFAULT_INTENT,
): ResolvedNotificationVariant => {
  const themeKey = !variant
    ? `${DEFAULT_VARIANT}${capitalize(DEFAULT_INTENT)}`
    : !HIERARCHY_KEYS.has(variant)
      ? variant // Already a combined legacy key — pass through unchanged
      : `${variant}${capitalize(intent)}` // New two-prop API

  const isBanner = themeKey.startsWith(BANNER_HIERARCHY)

  return { themeKey, isBanner }
}

/**
 * Q2 — Infers the layout from content composition. No `layout` prop exists; this
 * is the single source of truth for visual layout.
 *
 * - `action` present                       → `'button-down'`
 * - both `title` and `text` present        → `'multiline'`
 * - otherwise                              → `'one-line'`
 */
export const inferNotificationLayout = (args: {
  action?: unknown
  text?: unknown
  title?: unknown
  hasStatus?: boolean
}): NotificationLayout => {
  if (args.hasStatus) return 'one-line'
  if (args.action != null && args.action !== false && args.action !== '') return 'button-down'
  if (args.title != null && args.title !== '' && args.text != null && args.text !== '') return 'multiline'
  return 'one-line'
}

/**
 * Q4 — Auto-maps intent to the appropriate ARIA role / aria-live politeness.
 * `isLoading` forces `role="status"` / `aria-live="polite"` regardless of intent.
 */
export const resolveNotificationAria = (args: {
  intent?: NotificationIntent
  isLoading?: boolean
}): { role: 'status' | 'alert'; ariaLive: 'polite' | 'assertive' } => {
  if (args.isLoading) return { role: 'status', ariaLive: 'polite' }
  const isUrgent = args.intent === 'warning' || args.intent === 'danger'
  return isUrgent ? { role: 'alert', ariaLive: 'assertive' } : { role: 'status', ariaLive: 'polite' }
}
